import { useCallback, useState } from "react";

import type { OrderbookViewMode } from "../types/orderbook.types.ts";

const ORDERBOOK_VIEW_MODE_KEY = "trade_orderbook_view_mode";
const DEFAULT_ORDERBOOK_VIEW_MODE: OrderbookViewMode = "both";

function isOrderbookViewMode(value: unknown): value is OrderbookViewMode {
	return value === "both" || value === "bids" || value === "asks";
}

function readOrderbookViewMode(): OrderbookViewMode {
	if (typeof window === "undefined") return DEFAULT_ORDERBOOK_VIEW_MODE;

	try {
		const raw = window.localStorage.getItem(ORDERBOOK_VIEW_MODE_KEY);
		if (!raw) return DEFAULT_ORDERBOOK_VIEW_MODE;
		const parsed = JSON.parse(raw);
		return isOrderbookViewMode(parsed) ? parsed : DEFAULT_ORDERBOOK_VIEW_MODE;
	} catch {
		return DEFAULT_ORDERBOOK_VIEW_MODE;
	}
}

function writeOrderbookViewMode(value: OrderbookViewMode) {
	if (typeof window === "undefined") return;

	try {
		window.localStorage.setItem(ORDERBOOK_VIEW_MODE_KEY, JSON.stringify(value));
	} catch (error) {
		console.warn("Failed to persist orderbook view mode", error);
	}
}

export function useOrderbookViewMode() {
	const [viewMode, setViewModeState] = useState<OrderbookViewMode>(
		readOrderbookViewMode,
	);

	const setViewMode = useCallback((next: OrderbookViewMode) => {
		setViewModeState(next);
		writeOrderbookViewMode(next);
	}, []);

	return { viewMode, setViewMode };
}
